import { useEffect, useRef } from "react";
import { useParams, Navigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { updateFuelReportSettings } from "services/company.service";
import { FuelBarcodesPanel } from "features/fuel-barcodes/FuelBarcodesPanel";
import {
  SettingsCard,
  StatusBanner,
  useInlineEdit,
  useSaveWithToast,
} from "./ui";
import { TemplateSlotRow } from "./template-slot-row";
import { useTemplateSlotNames } from "./use-template-slot-names";
import {
  SlotFieldNames,
  readSlotValues,
  slotPayload,
  slotSaveErrorMessage,
} from "./template-slot-values";

// The company fields that hold the settings of the fuel report document.
const FUEL_REPORT_FIELDS: SlotFieldNames = {
  template: "fuelReportTemplate",
  source: "fuelReportTemplateSource",
  series: "fuelReportSeries",
  fileType: "fuelReportFileType",
  category: "fuelReportCategory",
};

interface FuelReportSettingsContainerProps {
  companyEntity: any;
  onRefresh: () => void;
}

export default function FuelReportSettingsContainer({
  companyEntity,
  onRefresh,
}: FuelReportSettingsContainerProps) {
  const { t } = useTranslation();
  const { companyId = "" } = useParams<{ companyId: string }>();
  const [searchParams] = useSearchParams();
  const names = useTemplateSlotNames(companyId);
  const { save, saving, error } = useSaveWithToast();

  const saved = readSlotValues(companyEntity, FUEL_REPORT_FIELDS);
  const edit = useInlineEdit(saved);

  // The list of what counts as fuel used to be a second tab; links such as
  // the one in the activity log still ask for it with ?subTab=barcodes.
  const barcodesRef = useRef<HTMLDivElement>(null);
  const wantsBarcodes = searchParams.get("subTab") === "barcodes";
  useEffect(() => {
    if (wantsBarcodes) barcodesRef.current?.scrollIntoView({ block: "start" });
  }, [wantsBarcodes]);

  const handleSave = async () => {
    const done = await save(
      async () => {
        await updateFuelReportSettings(
          slotPayload(FUEL_REPORT_FIELDS, edit.draft),
          companyId
        );
        onRefresh();
      },
      { errorMessage: (err) => slotSaveErrorMessage(err, t) }
    );
    if (done) edit.cancel();
  };

  return (
    <div className="space-y-5">
      {saved.source === "custom" && (
        <StatusBanner
          tone="warn"
          title={t("fuel_report_custom_template_warning")}
        />
      )}

      <SettingsCard>
        <TemplateSlotRow
          label={t("fuel_report_document_template")}
          saved={saved}
          names={names}
          draft={edit.editing ? edit.draft : null}
          onEdit={edit.start}
          onDraftChange={(patch) =>
            edit.setDraft((current) => ({ ...current, ...patch }))
          }
          onSave={handleSave}
          onCancel={edit.cancel}
          saving={saving}
          error={error}
        />
      </SettingsCard>

      <div ref={barcodesRef}>
        <FuelBarcodesPanel key={companyId} companyId={companyId} />
      </div>
    </div>
  );
}

export function FuelReportSettingsRedirect() {
  const { companyId } = useParams<{ companyId: string }>();
  return <Navigate to={`/app/${companyId}/settings/fuel`} replace />;
}
